import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Card, CardContent } from './ui/card';
import { Save, Plus, X } from 'lucide-react';
import apiClient from '../api/client';



function TemplateManager({ onSelectTemplate, isOpen, onOpenChange, mode = 'full' }) {
  const [templates, setTemplates] = useState([]);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newTemplate, setNewTemplate] = useState({
    name: '',
    terms_conditions: '',
    signature: '',
    bank_details: ''
  });

  useEffect(() => {
    if (isOpen) {
      fetchTemplates();
    }
  }, [isOpen]);

  const fetchTemplates = async () => {
    try {
      const response = await apiClient.get(`/quote-templates`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setTemplates(response.data);
    } catch (error) {
      console.error('Error fetching templates:', error);
    }
  };

  const createTemplate = async () => {
    try {
      await apiClient.post(`/quote-templates`, newTemplate, {
        headers: { 
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}` 
        }
      });
      setNewTemplate({ name: '', terms_conditions: '', signature: '', bank_details: '' });
      setShowCreateForm(false);
      fetchTemplates();
    } catch (error) {
      console.error('Error creating template:', error);
    }
  };

  const deleteTemplate = async (templateId) => {
    try {
      await apiClient.delete(`/quote-templates/${templateId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      fetchTemplates();
    } catch (error) {
      console.error('Error deleting template:', error);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent 
		className="max-w-4xl max-h-[80vh] overflow-y-auto"
		aria-describedby="template-manager-description"
      >
	    <DialogHeader>
	      <DialogTitle>Quote Templates</DialogTitle>
		</DialogHeader>
		  
		{/* Add a hidden description for screen readers */}
		<div id="template-manager-description" className="sr-only">
		  Manage your quote templates. Create new templates or select existing ones.
		</div>
        
        <Button onClick={() => setShowCreateForm(true)} className="mb-4">
          <Plus className="mr-2" size={16} /> Create New Template
        </Button>

        {showCreateForm && (
          <Card className="mb-6">
            <CardContent className="p-4">
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-semibold">Create New Template</h3>
                <Button variant="ghost" size="icon" onClick={() => setShowCreateForm(false)}>
                  <X size={16} />
                </Button>
              </div>
              
              <div className="space-y-4">
                <div>
                  <Label>Template Name</Label>
                  <Input
                    value={newTemplate.name}
                    onChange={(e) => setNewTemplate({ ...newTemplate, name: e.target.value })}
                  />
                </div>

                {/* TERMS-ONLY MODE */}
                {(mode === 'full' || mode === 'terms') && (
                  <div>
                    <Label>Terms &amp; Conditions</Label>
                    <Textarea
                      value={newTemplate.terms_conditions}
                      onChange={(e) =>
                        setNewTemplate({ ...newTemplate, terms_conditions: e.target.value })
                      }
                      rows={4}
                    />
                  </div>
                )}

                {/* FOOTER-ONLY MODE (Signature + Bank) */}
                {(mode === 'full' || mode === 'footer') && (
                  <>
                    <div>
                      <Label>Signature</Label>
                      <Textarea
                        value={newTemplate.signature}
                        onChange={(e) =>
                          setNewTemplate({ ...newTemplate, signature: e.target.value })
                        }
                        rows={2}
                      />
                    </div>

                    <div>
                      <Label>Bank Details</Label>
                      <Textarea
                        value={newTemplate.bank_details}
                        onChange={(e) =>
                          setNewTemplate({ ...newTemplate, bank_details: e.target.value })
                        }
                        rows={3}
                      />
                    </div>
                  </>
                )}

                <Button onClick={createTemplate}>
                  <Save className="mr-2" size={16} /> Save Template
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {templates.map(template => (
			// Update the template card content to show multi-line text
			<Card key={template.template_id} className="cursor-pointer hover:bg-gray-50">
			  <CardContent className="p-4" onClick={() => onSelectTemplate(template)}>
				<div className="flex justify-between items-start mb-2">
				  <h4 className="font-semibold">{template.name}</h4>
				  <Button 
					variant="ghost" 
					size="icon" 
					onClick={(e) => {
					  e.stopPropagation();
					  deleteTemplate(template.template_id);
					}}
				  >
					<X size={16} />
				  </Button>
				</div>
				
                {/* Show only the pieces relevant to the current mode */}
                {(mode === 'full' || mode === 'terms') && template.terms_conditions && (
                  <div className="text-sm text-gray-600 mb-2">
                    <strong>Terms:</strong>
                    <div className="whitespace-pre-wrap font-mono text-xs mt-1">
                      {template.terms_conditions}
                    </div>
                  </div>
                )}

                {(mode === 'full' || mode === 'footer') && template.signature && (
                  <div className="text-sm text-gray-600 mb-2">
                    <strong>Signature:</strong>
                    <div className="whitespace-pre-wrap font-mono text-xs mt-1">
                      {template.signature}
                    </div>
                  </div>
                )}

                {(mode === 'full' || mode === 'footer') && template.bank_details && (
                  <div className="text-sm text-gray-600">
                    <strong>Bank Details:</strong>
                    <div className="whitespace-pre-wrap font-mono text-xs mt-1">
                      {template.bank_details}
                    </div>
                  </div>
                )}

			  </CardContent>
			</Card>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default TemplateManager;